'use client';

import { cn } from '@core/utils';
import type { Editor } from '@tiptap/core';
import ListItem from '@tiptap/extension-list-item';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import type { EditorOptions, Extensions } from '@tiptap/react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { ReactNode } from 'react';
import { createContext } from 'react';

import { ImageButton } from './_components/ImageButton';
import { TEXT_ALIGN } from './_constants';
import BoxedText from './_utils/BoxedText';
import { CustomIframe } from './_utils/CustomIframe';
import { CustomImage } from './_utils/CustomImage';
import { CustomLink } from './_utils/CustomLink';
import { CustomVideo } from './_utils/CustomVideo';
import { FontSize } from './_utils/FontSize';
import { FontWeight } from './_utils/FontWeight';
import { Highlight } from './_utils/Highlight';
import { PasteFilter } from './_utils/PasteFilter';
import { TextColor } from './_utils/TextColor';
import { BizToolbar } from './BizToolbar';
import { Contents } from './Contents';
import { Toolbar } from './Toolbar';
export type { ToolbarProps as CustomEditorBizToolbarProps } from './BizToolbar';
export type { CustomEditorContentsProps } from './Contents';
export type { ToolbarProps as CustomEditorToolbarProps } from './Toolbar';

export interface CustomEditorProviderProps extends Partial<
  Pick<
    EditorOptions,
    'onUpdate' | 'content' | 'onFocus' | 'onBlur' | 'onCreate'
  >
> {
  children?: ReactNode;
  placeholder?: string;
  placeholderClass?: string;
  extraExtensions?: Extensions;
}

interface CustomEditorContextValue {
  editor: Editor | null;
}

export const CustomEditorContext = createContext<CustomEditorContextValue>({
  editor: null,
});

const FIXED_EXTENSIONS: Extensions = [
  FontWeight,
  Highlight,
  BoxedText,
  FontSize,
  CustomLink,
  PasteFilter,
  CustomImage,
  CustomIframe,
  CustomVideo,
  TextColor,
  TextStyle.configure({
    HTMLAttributes: { types: [ListItem.name, StarterKit.name] },
  }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    heading: {
      levels: [2, 3, 4, 5, 6],
    },
    paragraph: {
      HTMLAttributes: {
        class: 'min-h-24 typo-body3 font-400 Gray-black',
      },
    },
    horizontalRule: {
      HTMLAttributes: {
        class: 'border-t-1 border-Gray-300 my-30',
      },
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: true,
    },
  }),
];

const Provider = ({
  children,
  placeholder = '',
  placeholderClass,
  extraExtensions = [],
  ...editorProps
}: CustomEditorProviderProps) => {
  const editor = useEditor({
    ...editorProps,
    immediatelyRender: false,
    extensions: FIXED_EXTENSIONS.concat([
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: TEXT_ALIGN,
      }),
      Placeholder.configure({
        placeholder,
        emptyNodeClass: cn(
          'first:before:pointer-events-none first:before:float-left first:before:text-Gray-500 first:before:content-[attr(data-placeholder)]',
          placeholderClass,
        ),
      }),
      ...extraExtensions,
    ]),
  });

  return (
    <CustomEditorContext.Provider
      value={{
        editor,
      }}
    >
      {children}
    </CustomEditorContext.Provider>
  );
};

export const CustomEditor = {
  Provider,
  Toolbar,
  BizToolbar,
  Contents,
  ImageButton,
};
